/**
 * The scene outliner: a live tree of what's in the scene.
 *
 * Grouping made this necessary. A flat list of ids was fine when everything sat
 * in one bucket, but once objects can be nested you need to see the shape of
 * what you've built — and on a phone, `listObjects` in a terminal is not that.
 *
 * Clicking a row selects it; double-clicking frames it, which doubles as
 * "where the hell is cylinder_4".
 */

/** Rows are keyed by type so the tree scans without reading every label. */
const GLYPHS = {
  group: '▣',
  box: '▢',
  sphere: '●',
  cylinder: '▮',
};

export function createOutliner({ registry, selection, onSelect = () => {}, onFrame = () => {} } = {}) {
  const root = document.getElementById('outliner');
  const header = document.getElementById('outliner-header');
  const tree = document.getElementById('outliner-tree');
  const count = document.getElementById('outliner-count');
  let rovingId = null;

  tree.setAttribute('role', 'listbox');
  tree.setAttribute('aria-label', 'Scene objects');

  // A phone viewport is mostly viewport. Start collapsed where the panel would
  // otherwise cover a meaningful slice of the scene.
  if (window.innerWidth < 640) setOpen(false);

  function setOpen(open) {
    root.dataset.open = String(open);
    header.setAttribute('aria-expanded', String(open));
  }

  header.addEventListener('click', () => setOpen(root.dataset.open !== 'true'));

  function render() {
    const objects = registry.list();
    count.textContent = String(objects.length);

    const focusedId = tree.contains(document.activeElement)
      ? document.activeElement.closest?.('.outliner-row')?.dataset.id ?? null
      : null;
    if (focusedId) rovingId = focusedId;
    const objectIds = new Set(objects.map((object) => object.id));
    if (!objectIds.has(rovingId)) {
      rovingId = objectIds.has(selection?.activeId) ? selection.activeId : objects[0]?.id ?? null;
    }

    tree.replaceChildren();

    if (objects.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'outliner-empty';
      empty.textContent = 'empty';
      tree.append(empty);
      return;
    }

    // registry.list() walks the scene graph parents-first, so depth can be read
    // off each object's parent without building a tree separately.
    const depths = new Map();
    for (const object of objects) {
      const depth = object.parent ? (depths.get(object.parent) ?? 0) + 1 : 0;
      depths.set(object.id, depth);
      tree.append(row(object, depth));
    }

    if (focusedId && objectIds.has(focusedId)) {
      tree.querySelector(`.outliner-row[data-id="${CSS.escape(focusedId)}"]`)?.focus({ preventScroll: true });
    }
  }

  function row(object, depth) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'outliner-row';
    button.dataset.id = object.id;
    button.tabIndex = object.id === rovingId ? 0 : -1;
    button.setAttribute('role', 'option');
    const selected = selection?.ids.includes(object.id) ?? false;
    button.classList.toggle('selected', selected);
    button.classList.toggle('active', selection?.activeId === object.id);
    button.setAttribute('aria-selected', String(selected));
    button.style.paddingLeft = `${6 + depth * 13}px`;
    button.title = `${object.id} — click to select; double-click to frame`;

    const glyph = document.createElement('span');
    glyph.textContent = GLYPHS[object.type] ?? '•';
    glyph.style.color = object.color ?? 'var(--ui-muted)';

    const label = document.createElement('span');
    label.className = 'label';
    // The name is usually the id; show it only when someone has renamed it.
    label.textContent = object.name && object.name !== object.id ? object.name : object.id;

    button.append(glyph, label);

    if (object.type === 'group') {
      const kind = document.createElement('span');
      kind.className = 'kind';
      kind.textContent = `${object.children.length}`;
      button.append(kind);
    }

    button.addEventListener('click', (event) => {
      rovingId = object.id;
      onSelect(object.id, event);
    });
    button.addEventListener('dblclick', () => onFrame(object.id));
    return button;
  }

  function keydown(event) {
    if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
    const rows = [...tree.querySelectorAll('.outliner-row')];
    if (rows.length === 0) return;
    event.preventDefault();
    const current = event.target.closest?.('.outliner-row');
    const currentIndex = Math.max(0, rows.indexOf(current));
    const nextIndex = event.key === 'Home' ? 0
      : event.key === 'End' ? rows.length - 1
        : event.key === 'ArrowUp' ? Math.max(0, currentIndex - 1)
          : Math.min(rows.length - 1, currentIndex + 1);
    const next = rows[nextIndex];
    rovingId = next.dataset.id;
    rows.forEach((item) => { item.tabIndex = item === next ? 0 : -1; });
    next.focus();
  }

  tree.addEventListener('keydown', keydown);

  return { render, toggle: () => setOpen(root.dataset.open !== 'true') };
}
